import React, { useState } from 'react';
import { X, Plus, Download, Edit3, Copy, Trash2, ShieldAlert, AlertCircle, ArrowLeft } from 'lucide-react';
import { useEditorConfig } from '../../../../contexts/EditorContext';
import { NamedRuleSet } from '../../../../types';
import { validateRuleSet } from '../../../../utils/errorRuleset';
import { ERROR_RULESET_PRESETS } from '../../../../constants/errorRulesetPresets';
import { RuleEditor } from './RuleEditor';
import { INPUT_CLASS } from './uiConstants';

interface RuleSetLibraryModalProps {
  onClose: () => void;
}

export const RuleSetLibraryModal: React.FC<RuleSetLibraryModalProps> = ({ onClose }) => {
  const { errorRulesets: lib } = useEditorConfig();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showPresets, setShowPresets] = useState(false);

  if (!lib) return null;

  const list = lib.list;
  const editing = editingId ? list.find(r => r.id === editingId) : undefined;

  const createBlank = () => {
    const created = lib.create({ name: `Ruleset ${list.length + 1}`, rules: [], source: 'custom' });
    setEditingId(created.id);
  };
  const createFromPreset = (key: string) => {
    const preset = ERROR_RULESET_PRESETS.find(p => p.key === key);
    if (!preset) return;
    const created = lib.create({ name: preset.name, rules: JSON.parse(JSON.stringify(preset.rules)), source: `from ${preset.name}` });
    setShowPresets(false);
    setEditingId(created.id);
  };
  const duplicate = (rs: NamedRuleSet) => {
    const created = lib.create({ name: `${rs.name} (copy)`, rules: JSON.parse(JSON.stringify(rs.rules)), source: rs.source });
    setEditingId(created.id);
  };
  const remove = (rs: NamedRuleSet) => {
    const usage = lib.usageCount(rs.id);
    if (usage > 0) {
      alert(`«${rs.name}» is used by ${usage} node${usage === 1 ? '' : 's'}. Reassign those tasks (to another ruleset, Inline, or Default) before deleting.`);
      return;
    }
    lib.remove(rs.id);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-slate-950 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col border border-slate-200 dark:border-slate-800" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            {editing && (
              <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:text-slate-600" title="Back to list"><ArrowLeft size={18} /></button>
            )}
            <ShieldAlert size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">
              {editing ? `Edit «${editing.name}»` : 'Error Rulesets (project)'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!editing ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <button onClick={createBlank} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700">
                  <Plus size={14} /> New
                </button>
                <div className="relative">
                  <button onClick={() => setShowPresets(s => !s)} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:border-teal-400">
                    <Download size={14} /> From preset ▾
                  </button>
                  {showPresets && (
                    <div className="absolute left-0 mt-1 z-10 w-64 max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-1">
                      {ERROR_RULESET_PRESETS.map(p => (
                        <button key={p.key} onClick={() => createFromPreset(p.key)} className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800">
                          <div className="text-xs font-bold text-slate-700 dark:text-slate-200">{p.name}</div>
                          <div className="text-[10px] text-slate-400 leading-tight">{p.description}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {list.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center text-sm text-slate-400">
                  No project rulesets yet. Create one, or seed from a preset — then bind it from any ServiceTask's Error Handling tab.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-800">
                      <th className="py-2">Name</th>
                      <th className="py-2 w-16">Rules</th>
                      <th className="py-2 w-24">Used by</th>
                      <th className="py-2">Source</th>
                      <th className="py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(rs => {
                      const usage = lib.usageCount(rs.id);
                      const errs = validateRuleSet(rs.rules).filter(i => i.severity === 'error').length;
                      return (
                        <tr key={rs.id} className="border-b border-slate-100 dark:border-slate-800/50">
                          <td className="py-2 font-medium text-slate-700 dark:text-slate-200">
                            «{rs.name}»
                            {errs > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-red-600"><AlertCircle size={10} />{errs}</span>}
                          </td>
                          <td className="py-2 text-slate-500">{rs.rules.length}</td>
                          <td className="py-2 text-slate-500">{usage} node{usage === 1 ? '' : 's'}</td>
                          <td className="py-2 text-slate-400 text-xs">{rs.source || 'custom'}</td>
                          <td className="py-2">
                            <div className="flex items-center gap-1 justify-end text-slate-400">
                              <button onClick={() => setEditingId(rs.id)} className="p-1 hover:text-teal-600" title="Edit"><Edit3 size={14} /></button>
                              <button onClick={() => duplicate(rs)} className="p-1 hover:text-teal-600" title="Duplicate"><Copy size={14} /></button>
                              <button onClick={() => remove(rs)} className={`p-1 ${usage > 0 ? 'opacity-40 cursor-not-allowed' : 'hover:text-red-500'}`} title={usage > 0 ? 'In use — reassign first' : 'Delete'}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Name</label>
                <input value={editing.name} onChange={(e) => lib.update(editing.id, { name: e.target.value })} className={INPUT_CLASS} />
              </div>
              <RuleEditor
                rules={editing.rules}
                onChange={(rules) => lib.update(editing.id, { rules })}
                heading={<span className="text-[11px] text-slate-400">used by {lib.usageCount(editing.id)} node(s) — edits apply to all of them</span>}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
